import React, { useState } from "react";

import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  BULLETIN_FORM_ROUTE,
  bulletinEditPath,
} from "@/components/alerts/add-v2/routePaths";

import { EntryCard } from "./EntryCard";
import { ReportHandoff } from "./ReportHandoff";
import { UploadBulletinDialog } from "./UploadBulletinDialog";

/**
 * Where a bulletin starts.
 *
 * Six sources; two work today. The other four are deliberately visible in their not-configured
 * state — see `EntryCard`.
 *
 * A **seventh** arrives without being chosen: a RubiREPORT occurrence hands off with `?token=` on
 * this route, and there is nothing to pick, so the cards give way to `ReportHandoff`. It lands here
 * rather than on the form route because the row is created server-side and the officer ends up on
 * the edit route — this is the page that already means "a bulletin is starting" (decision 53).
 */
export const StartScreen: React.FC = () => {
  const { t } = useTranslation("bulletin/create-v2");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [uploadOpen, setUploadOpen] = useState(false);

  const openForm = () => navigate(BULLETIN_FORM_ROUTE);

  const reportToken = searchParams.get("token");
  if (reportToken) return <ReportHandoff token={reportToken} />;

  return (
    <Box
      sx={{
        maxWidth: "var(--page-max-width)",
        mx: "auto",
        px: { xs: 2, sm: 3 },
        py: 4,
      }}
    >
      <Typography variant="h5" fontWeight={600}>
        {t("start.heading")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t("start.subheading")}
      </Typography>

      <Box
        sx={{
          mt: 3,
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "repeat(3, 1fr)",
          },
        }}
      >
        <EntryCard
          configured
          title={t("start.scratch_title")}
          body={t("start.scratch_body")}
          onClick={openForm}
        />
        <EntryCard
          configured
          title={t("start.upload_title")}
          body={t("start.upload_body")}
          onClick={() => setUploadOpen(true)}
        />
        <EntryCard
          configured={false}
          badge={t("start.concept_preview")}
          title={t("start.describe_title")}
          body={t("start.describe_body")}
        />
        <EntryCard
          configured={false}
          title={t("start.report_title")}
          body={t("start.report_body")}
        />
        <EntryCard
          configured={false}
          title={t("start.rms_title")}
          body={t("start.rms_body")}
        />
        <EntryCard
          configured={false}
          title={t("start.email_title")}
          body={t("start.email_body")}
        />
      </Box>

      <UploadBulletinDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onImported={(bulletinId) => {
          setUploadOpen(false);
          // The edit route, because the extraction has already created the draft — the document
          // exists and this is a form opening onto it, which is what that route means (decision 31).
          navigate(bulletinEditPath(bulletinId));
        }}
      />
    </Box>
  );
};
